import { createClient } from '@supabase/supabase-js';
import { report } from '../sentry';
import { looksOffline } from './offline';
import type { Session, SupabaseClient } from '@supabase/supabase-js';

/** Клиент Supabase на всё приложение.
 *
 *  Ленивый и единственный. Второй экземпляр означал бы две копии состояния
 *  сессии в одной вкладке: обновление токена в одной из них оставляло бы
 *  другую с протухшим, и запросы начинали бы отказывать через час после
 *  входа — ровно тогда, когда ребёнок сел заниматься. */
let client: SupabaseClient | null = null;

export function authConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
}

export function supabase(): SupabaseClient {
  if (client) return client;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  /** Отказ, а не молчаливая заглушка. Приложение с обязательным входом и
   *  без адреса сервера не работает вовсе, и узнать об этом надо на первом
   *  экране, а не по пустому списку профилей. */
  if (!url || !key) throw new Error('нет адреса или ключа Supabase');
  client = createClient(url, key, {
    auth: {
      /** PKCE, а не implicit. При implicit-потоке Supabase возвращает
       *  токены во ФРАГМЕНТЕ адреса — то есть действующий доступ к учётной
       *  записи родителя оказывается в строке браузера, откуда его подберут
       *  и хлебные крошки Sentry, и повтор сессии. При PKCE в адресе
       *  одноразовый код, который без хранящегося в этом же браузере
       *  проверочного значения не стоит ничего. */
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return client;
}

/** Только для тестов: сбросить закешированный клиент. */
export function resetClient(): void {
  client = null;
}

export type { Session };

/** Русские тексты вместо английских из GoTrue.
 *
 *  Родитель видит эти строки в момент, когда у него что-то не получилось, —
 *  то есть в единственный момент, когда текст ошибки вообще читают. «Invalid
 *  login credentials» здесь означает, что дальше он не пойдёт. */
/** Уже отправленные наружу незнакомые отказы.
 *
 *  Ограничение стоит не от жадности, а от формы этой функции: у неё имя
 *  «текст отказа», а внутри побочное действие. Сегодня все семь мест зовут
 *  её из обработчиков — то есть один раз на один отказ, — но следующий
 *  вызывающий может позвать её прямо в разметке, и тогда отправка пойдёт на
 *  КАЖДУЮ отрисовку: мигающий курсор в поле ввода, тик таймера, повтор
 *  StrictMode. Экран при этом здоров, а настоящие отказы тонут в шуме.
 *
 *  Множество живёт до перезагрузки вкладки. Этого достаточно: смысл события
 *  в том, чтобы отказ стал ИЗВЕСТЕН, а не в подсчёте повторов. */
const reported = new Set<string>();

function reportUnknownOnce(key: string, error: unknown): void {
  if (reported.has(key)) return;
  reported.add(key);
  report(error, 'authErrorText:неизвестный отказ');
}

export function authErrorText(error: unknown): string {
  const raw = String((error as { message?: string })?.message ?? error);
  const code = String((error as { code?: string })?.code ?? '');
  const has = (needle: string) => raw.toLowerCase().includes(needle);

  if (has('invalid login credentials')) return 'Неверная почта или пароль.';
  if (has('email not confirmed')) {
    /** Ссылки в письмах больше нет — текст, зовущий её нажать, отправлял бы
     *  родителя искать то, чего не существует. */
    return 'Почта ещё не подтверждена. Введите код из письма.';
  }
  if (has('user already registered') || code === 'user_already_exists') {
    return 'Такая почта уже зарегистрирована. Войдите или восстановите пароль.';
  }
  if (has('token has expired or is invalid')) {
    return 'Код не подошёл: он истёк или неверен. Запросите новый.';
  }
  if (has('password should be at least')) return 'Пароль короче восьми знаков.';
  /** Пароль отвергнут по СОДЕРЖАНИЮ, а не по длине. Сюда попадают две
   *  настройки сразу: требования к знакам (`password_requirements`,
   *  доступна и на бесплатном тарифе) и проверка по базе утечек (нужен
   *  платный). Обе дают код `weak_password`.
   *
   *  Ветка написана ЗАРАНЕЕ, до включения любой из них, и это не запас на
   *  будущее, а разоружение ловушки. Без неё отказ уезжал в общую ветку
   *  «мы пока не знаем почему… напишите на privacy@mathpaw.com» — то есть
   *  родителю, которому надо всего лишь придумать другой пароль,
   *  предлагали писать на ящик, который тогда писем не принимал. Тупик,
   *  созданный собственной настройкой.
   *
   *  Текст не называет, ЧЕМ именно плох пароль: у двух настроек причины
   *  разные («мало разных знаков» и «этот пароль уже утекал»), а GoTrue
   *  различает их только английской строкой, читать которую родителю
   *  незачем. Совет один и тот же и работает в обоих случаях. */
  if (code === 'weak_password' || has('password is known to be weak')
      || has('password should contain at least')) {
    return 'Такой пароль не подойдёт: он слишком простой или уже встречался в утечках. Придумайте другой — длиннее и не из словаря.';
  }
  if (has('new password should be different')) {
    return 'Новый пароль совпадает с прежним. Придумайте другой.';
  }
  if (has('for security purposes') || has('rate limit') || code === 'over_email_send_rate_limit') {
    return 'Слишком часто. Подождите минуту и попробуйте снова.';
  }
  if (has('unable to validate email') || has('invalid format')) {
    return 'Проверьте адрес почты.';
  }
  if (looksOffline(error)) {
    /** Список признаков сети ОДИН на всё приложение (`data/offline.ts`).
     *  Здесь стоял свой, из четырёх слов, и он расходился с общим на пять:
     *  таймаут, обрыв, `econn`, `enotfound` и вообще всё, что внятно только
     *  кодом или именем ошибки, а не текстом.
     *
     *  Измерено: шесть видов сетевого отказа из семи уходили в ветку «мы
     *  пока не знаем почему» — ту самую, что зовёт писать на
     *  privacy@mathpaw.com, ящик, тогда писем не принимавший. Родителю, у
     *  которого всего лишь метро или лифт, называли выход, которого нет.
     *
     *  Вторая цена — шум: каждый такой отказ уходил в Sentry как
     *  неизвестный, и настоящие неизвестные тонули бы в туннелях метро.
     *
     *  Сюда же попадает запрос, срезанный расширением браузера: для него
     *  это тот же оборванный `fetch`. Отличить одно от другого на клиенте
     *  нечем, поэтому текст говорит про обе причины. */
    return 'Не удалось связаться с сервером. Проверьте интернет и расширения браузера.';
  }
  if (has('code verifier') || has('flow state') || has('invalid flow')) {
    /** Состояние потока PKCE живёт в хранилище браузера и теряется при
     *  его чистке, в другом браузере и в приватном окне. Отдельный текст
     *  нужен потому, что родитель видит «код не подошёл» и ищет ошибку в
     *  цифрах, а цифры верные. */
    return 'Похоже, страницу открыли в другом браузере или очистили данные. Нажмите «Прислать новый код».';
  }
  /** Отказ, которого мы не знаем.
   *
   *  Прежде здесь было молчание в обе стороны: родитель видел «попробуйте
   *  ещё раз», а наружу не уходило ничего — то есть узнать, что именно
   *  отвергает GoTrue, было неоткуда вообще. Поймано живым прогоном:
   *  регистрация с одним адресом отказала, и причину пришлось доставать
   *  подменой `fetch` в консоли браузера.
   *
   *  Своя ветка появится тогда, когда такой отказ приедет в Sentry и станет
   *  понятно, что он значит. До тех пор текст не обещает, что повтор
   *  поможет: у постоянного отказа он не поможет никогда, а совет
   *  «попробуйте ещё раз» уводит родителя по кругу. */
  reportUnknownOnce(raw, error);
  return 'Не получилось, и мы пока не знаем почему. Мы уже знаем об этой ошибке. Если повтор не помогает — напишите на privacy@mathpaw.com.';
}
